/**
 * EM/Lead Portal — Dashboard
 * Engagement Manager overview: client engagements, pending reviews, playback status.
 * Stat cards are clickable with drill-down into the underlying data.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, CircularProgress, Chip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton,
} from '@mui/material';
import {
  Assessment, PlayCircle, Description, People, Close,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import PortalTooltip from '../../../components/PortalTooltip';
import RefreshButton from '../../../components/RefreshButton';

interface DashboardStats {
  totalAssessments: number;
  pendingReports: number;
  scheduledPlaybacks: number;
  assignedClients: number;
}

interface DetailData {
  title: string;
  items: any[];
  columns: { key: string; label: string }[];
}

/* Raw API responses cached so drill-down doesn't re-fetch */
interface RawData {
  assessments: any[];
  sessions: any[];
  organizations: any[];
}

const StatCard: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  onClick: () => void;
  tooltipKey?: string;
}> = ({ label, value, icon, color, onClick, tooltipKey }) => {
  const card = (
    <Paper
      onClick={onClick}
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}15`,
          color,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
          Click for details →
        </Typography>
      </Box>
    </Paper>
  );

  if (tooltipKey) {
    return <PortalTooltip tooltipKey={tooltipKey}>{card}</PortalTooltip>;
  }
  return card;
};

const EMDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalAssessments: 0,
    pendingReports: 0,
    scheduledPlaybacks: 0,
    assignedClients: 0,
  });
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState<RawData>({ assessments: [], sessions: [], organizations: [] });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<DetailData | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([
      authFetch('/api/sme/assessments').then((r) => r.json()).catch(() => ({ assessments: [] })),
      authFetch('/api/sme/playback-sessions').then((r) => r.json()).catch(() => ({ sessions: [] })),
      authFetch('/api/sme/client-organizations').then((r) => r.json()).catch(() => ({ organizations: [] })),
    ]).then(([assessData, playbackData, orgData]) => {
      const assessments = assessData.assessments || [];
      const sessions = playbackData.sessions || [];
      const orgs = orgData.organizations || [];
      setRawData({ assessments, sessions, organizations: orgs });
      setStats({
        totalAssessments: assessments.length,
        pendingReports: assessments.filter((a: any) => a.report_status !== 'Complete').length,
        scheduledPlaybacks: sessions.filter((s: any) => s.status === 'scheduled').length,
        assignedClients: orgs.length,
      });
      setLastRefreshed(new Date());
    }).finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCardClick = (cardType: string) => {
    if (cardType === 'assessments') {
      setDetailData({
        title: 'Total Assessments',
        items: rawData.assessments,
        columns: [
          { key: 'domain', label: 'Domain' },
          { key: 'category', label: 'Category' },
          { key: 'domain_score', label: 'Score' },
          { key: 'report_status', label: 'Report Status' },
          { key: 'created_at', label: 'Date' },
        ],
      });
    } else if (cardType === 'reports') {
      const pending = rawData.assessments.filter(
        (a: any) => a.report_status !== 'Complete',
      );
      setDetailData({
        title: 'Pending Reports',
        items: pending,
        columns: [
          { key: 'domain', label: 'Domain' },
          { key: 'category', label: 'Category' },
          { key: 'report_status', label: 'Report Status' },
          { key: 'created_at', label: 'Date' },
        ],
      });
    } else if (cardType === 'playbacks') {
      const scheduled = rawData.sessions.filter((s: any) => s.status === 'scheduled');
      setDetailData({
        title: 'Scheduled Playbacks',
        items: scheduled,
        columns: [
          { key: 'assessment_domain', label: 'Domain' },
          { key: 'organization_name', label: 'Client' },
          { key: 'scheduled_date', label: 'Scheduled Date' },
          { key: 'status', label: 'Status' },
          { key: 'meeting_link', label: 'Meeting' },
        ],
      });
    } else if (cardType === 'clients') {
      setDetailData({
        title: 'Assigned Clients',
        items: rawData.organizations,
        columns: [
          { key: 'name', label: 'Organization' },
          { key: 'domain', label: 'Domain' },
          { key: 'is_active', label: 'Status' },
        ],
      });
    }
    setDetailOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#C6007E' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          KPMG EM/Lead Dashboard
        </Typography>
        <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage client engagements, review reports, and oversee assessment delivery.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
        <Chip label="Engagement Manager" sx={{ backgroundColor: '#C6007E', color: '#fff', fontWeight: 600 }} />
      </Stack>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total Assessments"
            value={stats.totalAssessments}
            icon={<Assessment />}
            color="#C6007E"
            onClick={() => handleCardClick('assessments')}
            tooltipKey="em.dashboard.total_assessments"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Pending Reports"
            value={stats.pendingReports}
            icon={<Description />}
            color="#483698"
            onClick={() => handleCardClick('reports')}
            tooltipKey="em.dashboard.pending_reports"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Scheduled Playbacks"
            value={stats.scheduledPlaybacks}
            icon={<PlayCircle />}
            color="#00338D"
            onClick={() => handleCardClick('playbacks')}
            tooltipKey="em.dashboard.scheduled_playbacks"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Assigned Clients"
            value={stats.assignedClients}
            icon={<People />}
            color="#00B8F1"
            onClick={() => handleCardClick('clients')}
            tooltipKey="em.dashboard.assigned_clients"
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          EM/Lead Role Responsibilities
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#C6007E' }}>
              Client Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Assign assessment domains to client organizations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Schedule and manage playback sessions with clients
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Deliver final assessment reports to clients
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#C6007E' }}>
              Quality Assurance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Review and approve draft assessment reports
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Finalize reports before client delivery
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • Upload knowledge assets for the engagement team
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Detail Drill-down Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {detailData?.title || 'Details'}
          <IconButton onClick={() => setDetailOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          {detailData && detailData.items.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  {detailData.columns.map((col) => (
                    <TableCell key={col.key} sx={{ fontWeight: 600 }}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {detailData.items.map((item, idx) => (
                  <TableRow key={idx}>
                    {detailData.columns.map((col) => (
                      <TableCell key={col.key}>
                        {col.key === 'status' || col.key === 'report_status' ? (
                          <Chip label={item[col.key] || '—'} size="small" />
                        ) : col.key === 'is_active' ? (
                          <Chip
                            label={item[col.key] === false ? 'Inactive' : 'Active'}
                            size="small"
                            color={item[col.key] === false ? 'default' : 'success'}
                          />
                        ) : col.key === 'created_at' || col.key === 'scheduled_date' ? (
                          item[col.key]
                            ? new Date(item[col.key]).toLocaleString()
                            : '—'
                        ) : col.key === 'domain_score' ? (
                          item[col.key] != null ? `${item[col.key]}%` : '—'
                        ) : col.key === 'meeting_link' ? (
                          item[col.key]
                            ? <a href={item[col.key]} target="_blank" rel="noopener noreferrer">Join</a>
                            : '—'
                        ) : (
                          item[col.key] ?? '—'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No data available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EMDashboard;
